import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Table } from 'antd';
import classnames from 'classnames';
import { useEffect, useMemo, useState, useContext } from 'react';
import { Badge, Spinner } from 'libs.nucleus';
import { COLORS_HEX } from 'libs.react';
import { DataReportsContext } from '../../../../contexts/data_reports/data_reports.context';
import { formatCubeValue, getLinkLabel, isLinkFormat, isNumericCubeFormat, toLinkHref, } from '../../../utils/cube_format.utils';
import { findDataIndex, parseISODate, parseISODateTime } from '../../../utils/data_reports.utils';
import { getColumnStyle, getConditionalStyleValue } from '../../../utils/style_helpers.utils';
// Map a stored color token to its design-system hex; leave non-token values (already hex, raw CSS) untouched.
const resolveColorToken = (token) => COLORS_HEX[token] || token;
const COLUMN_TYPE_STYLES = {
    cell: { containerClass: '-m-[16px] p-[16px]' },
    boolean: { containerClass: 'rounded-full size-7 mx-auto' },
    circle: { containerClass: 'rounded-full size-7 mx-auto flex justify-center items-center' },
    status: { containerClass: '' },
};
export const TableChart = ({ resultSets, style: columnsStyles, setSortingConfig, isLoading, roleBasedVisibility, labelOverrides, defaultSortedColumns, serverSidePagination, showFullTimestamp = true, }) => {
    // `applyValueFormatting` is true only for new-flow reports (opened with `rid`). Legacy reports skip
    // Cube numeric formatting and fall back to the pre-SSP-3972 raw value + suffix rendering.
    const { hasPolicy, applyValueFormatting } = useContext(DataReportsContext);
    const [currentSorterState, setCurrentSorterState] = useState('');
    const buildIndicatorMap = (cols) => {
        if (!cols || cols.length === 0) {
            return {};
        }
        return cols.reduce((acc, col) => {
            acc[col.key] = col.direction === 'asc' ? 'ascend' : 'descend';
            return acc;
        }, {});
    };
    // Controlled sort indicator state - syncs from defaultSortedColumns prop and
    // updates when the user clicks a column header.
    const [sortIndicators, setSortIndicators] = useState(() => buildIndicatorMap(defaultSortedColumns));
    useEffect(() => {
        setSortIndicators(buildIndicatorMap(defaultSortedColumns));
    }, [defaultSortedColumns]);
    const renderColumnContent = (value, columnStyle, column) => {
        const measureAnnotation = resultSets[0]?.annotation().measures[column.key];
        const dimensionAnnotation = resultSets[0]?.annotation().dimensions[column.key];
        const cubeFormat = (measureAnnotation ?? dimensionAnnotation)?.format;
        // Cube value formatting (%, K/M, decimals) only applies to new-flow reports (`rid`); legacy
        // reports render raw values with their plain suffix, exactly as before SSP-3972.
        const hasNumericCubeFormat = applyValueFormatting && isNumericCubeFormat(cubeFormat);
        // Preserve legacy behaviour (drop falsy values) but keep a real 0 when a numeric format can render it.
        const isZero = value === 0 || value === '0';
        if (!value && !(isZero && hasNumericCubeFormat)) {
            return null;
        }
        // Hyperlink-formatted string dimensions render as anchors. The visible text is a fixed label
        // ('Link') shared across the column — the raw value is used only as the href.
        if (isLinkFormat(cubeFormat)) {
            const href = toLinkHref(value);
            return (_jsx("a", { href: href, target: '_blank', rel: 'noopener noreferrer', className: 'text-primary-600 underline break-all', children: getLinkLabel(cubeFormat) }));
        }
        const suffix = measureAnnotation?.meta?.suffix;
        const hasPercentageSuffix = suffix?.includes('%');
        const hasPercentageUnit = columnStyle?.unit?.value?.includes('%');
        let formattedValue = value;
        let valueWithSuffix;
        if (hasNumericCubeFormat) {
            // The Cube format is authoritative — it already renders % / K-M-B etc., so skip the suffix logic.
            valueWithSuffix = formatCubeValue(value, cubeFormat);
        }
        else {
            // Legacy behaviour: round percentage values and append the annotation/unit suffix.
            if (hasPercentageSuffix || hasPercentageUnit) {
                const numericValue = typeof value === 'string' ? parseFloat(value) : value;
                if (typeof numericValue === 'number' && !isNaN(numericValue)) {
                    formattedValue = Math.round(numericValue * 100) / 100; // Round to 2 decimal places
                }
            }
            valueWithSuffix = suffix ? `${formattedValue}${suffix}` : formattedValue;
            // Check if column style has unit configuration
            if (columnStyle?.unit?.value && !suffix) {
                valueWithSuffix = (_jsxs(_Fragment, { children: [formattedValue, _jsx("span", { className: `ml-1 ${columnStyle.unit.size === 'base' ? 'text-sm' : 'text-xs'}`, children: columnStyle.unit.value })] }));
            }
        }
        if (!columnStyle) {
            return valueWithSuffix;
        }
        const { type, property } = columnStyle;
        // Conditional-format values are stored as color tokens (e.g. 'purple', 'greenDark', 'purple3').
        // Resolve them to their design-system hex before applying as inline CSS, otherwise the browser
        // renders the token as either a standard (much darker) CSS keyword or nothing at all. Callers that
        // already pass hex/raw CSS are unaffected because the lookup falls through to the original value.
        const propertyValue = resolveColorToken(getConditionalStyleValue(value, columnStyle));
        return (_jsx("div", { className: classnames(COLUMN_TYPE_STYLES[type]?.containerClass), style: property ? { [property]: propertyValue } : undefined, children: type === 'status' ? (_jsx(Badge, { variant: 'common', label: valueWithSuffix })) : type === 'boolean' ? null : (valueWithSuffix) }));
    };
    // Check if a column should be visible based on user role
    const isColumnVisibleByRole = (columnKey) => {
        if (!roleBasedVisibility || roleBasedVisibility.length === 0) {
            return true; // If no role restrictions, show all columns
        }
        const roleConfig = roleBasedVisibility.find((config) => config.columnName === columnKey);
        if (!roleConfig) {
            return true; // If column not in role config, show it by default
        }
        return hasPolicy(roleConfig.requiredRole);
    };
    // Generate columns from result set
    const columns = useMemo(() => {
        return resultSets[0]
            ?.tableColumns(undefined)
            .map((column, index) => {
            const columnStyle = getColumnStyle(column.key, columnsStyles);
            const isColumnHidden = columnStyle?.hidden;
            const isColumnVisibleForUser = isColumnVisibleByRole(column.key);
            if (isColumnHidden || !isColumnVisibleForUser) {
                return null;
            }
            const restColumn = { ...column, children: undefined };
            const override = labelOverrides?.find((o) => o.column === column.key);
            return {
                ...restColumn,
                dataIndex: findDataIndex(column),
                title: override?.label || column.shortTitle,
                width: columnStyle?.width,
                sorter: {
                    multiple: index + 1,
                },
                sortOrder: sortIndicators[column.key] || undefined,
                render: (value) => {
                    let v = value;
                    const isDateTime = column.type === 'time';
                    if (isDateTime) {
                        v = showFullTimestamp ? parseISODateTime(value) : parseISODate(value);
                    }
                    return renderColumnContent(v, columnStyle, column);
                },
            };
        })
            .filter((column) => column !== null);
    }, [
        resultSets,
        columnsStyles,
        roleBasedVisibility,
        hasPolicy,
        labelOverrides,
        sortIndicators,
        showFullTimestamp,
        applyValueFormatting,
    ]);
    // Helper to parse sorter into order config
    const parseSorter = (sorterItem) => {
        const order = {};
        const cleanFieldName = (field) => {
            // Remove pivot prefix if present (e.g., "order.new,participant_statuses.c_status_update_time" -> "participant_statuses.c_status_update_time")
            if (field.includes(',')) {
                return field.split(',').pop() || field;
            }
            return field;
        };
        if (Array.isArray(sorterItem)) {
            sorterItem.forEach((sort) => {
                if (sort.field && sort.order) {
                    const cleanField = cleanFieldName(sort.field);
                    order[cleanField] = sort.order === 'ascend' ? 'asc' : 'desc';
                }
            });
        }
        else if (sorterItem.field && sorterItem.order) {
            const cleanField = cleanFieldName(sorterItem.field);
            order[cleanField] = sorterItem.order === 'ascend' ? 'asc' : 'desc';
        }
        return order;
    };
    // Handle table change events (pagination, sorting)
    const handleTableChange = (pagination, _filters, sorter) => {
        // Create a stable string representation of the current sorter state
        const sorterString = Array.isArray(sorter)
            ? sorter
                .map((s) => `${s.field}:${s.order}`)
                .sort()
                .join('|')
            : sorter && sorter.field && sorter.order
                ? `${sorter.field}:${sorter.order}`
                : '';
        const newPageIndex = (pagination.current ?? 1) - 1; // Ant uses 1-based, we use 0-based
        const newPageSize = pagination.pageSize ?? serverSidePagination?.paginationState.pageSize ?? 10;
        const sortChanged = sorterString !== currentSorterState;
        const paginationChanged = serverSidePagination &&
            (serverSidePagination.paginationState.pageIndex !== newPageIndex ||
                serverSidePagination.paginationState.pageSize !== newPageSize);
        // Only call setSortingConfig if the sorter state actually changed
        if (sortChanged) {
            setCurrentSorterState(sorterString);
            // Update visual sort indicators to match user's column click
            const sorters = Array.isArray(sorter) ? sorter : [sorter];
            const activeSorters = sorters.filter((s) => s.field && s.order);
            if (activeSorters.length > 0) {
                const newIndicators = {};
                activeSorters.forEach((s) => {
                    newIndicators[s.field] = s.order;
                });
                setSortIndicators(newIndicators);
            }
            else {
                setSortIndicators(buildIndicatorMap(defaultSortedColumns));
            }
            if (setSortingConfig) {
                setSortingConfig(parseSorter(sorter));
            }
            // Don't call onPaginationChange when sort changed - setSortingConfig (handleSortChange)
            // already resets to page 0 and executes the query. Calling onPaginationChange would
            // run executeQuery with stale tableSortingConfig and overwrite the correct sort.
        }
        else if (paginationChanged && serverSidePagination?.onPaginationChange) {
            serverSidePagination.onPaginationChange({
                pageIndex: newPageIndex,
                pageSize: newPageSize,
            });
        }
    };
    const dataSource = useMemo(() => {
        // Server handles pagination/sorting, we just display the data
        const rawData = resultSets.flatMap((rs) => rs?.tablePivot() || []);
        // Normalize null-like values to empty string for consistent display
        return rawData?.map((row) => {
            return Object.fromEntries(Object.entries(row).map(([key, value]) => {
                if (value === null || value === undefined || value === '') {
                    return [key, ''];
                }
                // Preserve 0 - it's a valid measure value
                return [key, value];
            }));
        });
    }, [resultSets]);
    // Build pagination config for server-side pagination
    const paginationConfig = useMemo(() => {
        if (!serverSidePagination) {
            return false; // Disable pagination if not provided
        }
        return {
            current: serverSidePagination.paginationState.pageIndex + 1, // Ant uses 1-based
            pageSize: serverSidePagination.paginationState.pageSize,
            total: serverSidePagination.total,
            pageSizeOptions: ['5', '10', '20', '50', '100'],
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        };
    }, [serverSidePagination]);
    return (_jsx(Table, { className: 'size-full', columns: columns, dataSource: dataSource, pagination: paginationConfig, onChange: handleTableChange, rowKey: (record, index) => {
            // Try to create a unique key from the first few values + index
            const firstValues = Object.values(record).slice(0, 3).join('|');
            return `${firstValues}-${index}`;
        }, loading: {
            spinning: isLoading,
            indicator: (_jsx("div", { className: 'flex items-center justify-center size-full', children: _jsx(Spinner, { size: 'xl' }) })),
        } }));
};
